import {
  INDUSTRIAL_CASE_BOARD_ID,
  parseIndustrialCaseBoardItem,
} from '@/domain/industrialCase/industrialCaseBoard';
import type { IndustrialCaseDocument } from '@/domain/industrialCase/industrialCaseDocument';

import { getBoardContentsByBdId } from '@/apis/boardContents';

/** How many cases one page of the board list can hold. */
const CASE_PAGE_SIZE = 100;

/**
 * ### fetchPublishedIndustrialCases
 *
 * Reads every published customer case from the board.
 *
 * Uses the unauthenticated `GET /board-contents` endpoint, which the API limits
 * to `status = 2` for non-admin callers. Drafts therefore cannot reach the public
 * site even if this is called from somewhere unexpected — the filter lives on the
 * server, not here.
 *
 * Rows whose `add_data` is not a case document are skipped rather than throwing,
 * so one malformed record cannot take the whole page down.
 *
 * @returns published case documents in board order
 */
export async function fetchPublishedIndustrialCases(): Promise<
  Array<IndustrialCaseDocument>
> {
  const response = await getBoardContentsByBdId({
    bd_id: INDUSTRIAL_CASE_BOARD_ID,
    take: CASE_PAGE_SIZE,
    page: 1,
    order: 'order:ASC',
    search: '',
    category: '',
  });

  return (response.results ?? []).flatMap((content) => {
    const item = parseIndustrialCaseBoardItem(content);

    return item ? [item.document] : [];
  });
}

/**
 * ### findPublishedIndustrialCase
 *
 * Looks up one published case by the slug in its URL and the language it is
 * rendered in.
 *
 * Language is part of the identity, not a filter applied afterwards: the same
 * case exists once per language and both share a slug, because the locale already
 * separates them in the URL. Matching on slug alone would hand a Korean document
 * to the English route.
 *
 * @param slug - the URL segment, which is the document's `slug`
 * @param language - the site language being rendered
 * @returns the matching document, or `undefined` when nothing is published for
 *   that slug and language — a route should turn this into a 404
 */
export async function findPublishedIndustrialCase(
  slug: string,
  language: IndustrialCaseDocument['language'],
): Promise<IndustrialCaseDocument | undefined> {
  const cases = await fetchPublishedIndustrialCases();

  return cases.find(
    (document) => document.slug === slug && document.language === language,
  );
}

/**
 * ### filterIndustrialCasesByLanguage
 *
 * Narrows cases to the ones authored for a given site language.
 *
 * Documents hold final prose rather than translation keys, so a case belongs to
 * exactly one language and cannot be rendered into another. A language with no
 * authored cases legitimately has none to show.
 *
 * @param cases - published documents
 * @param language - the site language being rendered
 * @returns the documents authored for that language
 */
export function filterIndustrialCasesByLanguage(
  cases: ReadonlyArray<IndustrialCaseDocument>,
  language: IndustrialCaseDocument['language'],
): Array<IndustrialCaseDocument> {
  return cases.filter((document) => document.language === language);
}
